import React from 'react';
import styled from 'styled-components';

const PLACEHOLDER_IMAGE = '/assets/crop-placeholder.png';

const SelectorWrapper = styled.div`
  margin: 20px 0;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
`;

const SelectorHeader = styled.div`
  margin-bottom: 16px;

  h5 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
  }

  p {
    font-size: 13px;
    color: #666;
    margin: 0;
  }
`;

const PoseSlotLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
  margin-top: ${(props) => (props.index > 0 ? '16px' : '0')};
`;

const CropOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
`;

const CropOptionCard = styled.div`
  border: 2px solid ${(props) => (props.selected ? '#479de3' : '#e0e0e0')};
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  background-color: ${(props) => (props.selected ? '#f5f9ff' : '#fff')};
  position: relative;

  &:hover {
    border-color: ${(props) => (props.selected ? '#479de3' : '#bbb')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ExampleImageContainer = styled.div`
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  overflow: hidden;
  border-radius: 4px;
  background-color: #f5f5f5;
`;

const ExampleImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const OptionName = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #333;
  margin-bottom: 2px;
`;

const OptionDescription = styled.div`
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
`;

const SelectedIndicator = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 20px;
  height: 20px;
  background-color: #479de3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

const CropOptionsPrepaySelector = ({
  cropOptions,
  posesCount,
  cropSelections,
  onCropSelectionsChange,
}) => {
  const handleSelect = (poseIndex, option) => {
    const updated = [...cropSelections];
    updated[poseIndex] = {
      id: option.id,
      identifier: option.identifier,
      displayName: option.display_name || option.displayName,
      description: option.description,
      labCode: option.lab_code || option.labCode,
      exampleImageUrl: option.example_image_url || option.exampleImageUrl,
    };
    onCropSelectionsChange(updated);
  };

  const slotsToRender = Math.max(posesCount, 1);

  return (
    <SelectorWrapper>
      <SelectorHeader>
        <h5>Select Crop Style{slotsToRender > 1 ? ' for Each Pose' : ''}</h5>
        <p>
          Choose how you'd like your photo{slotsToRender > 1 ? 's' : ''} to be
          cropped.
        </p>
      </SelectorHeader>

      {Array.from({ length: slotsToRender }, (_, poseIndex) => (
        <div key={`pose-slot-${poseIndex}`}>
          {slotsToRender > 1 && (
            <PoseSlotLabel index={poseIndex}>
              Pose {poseIndex + 1}
            </PoseSlotLabel>
          )}
          <CropOptionsGrid>
            {cropOptions.map((option) => {
              const isSelected = cropSelections[poseIndex]?.id === option.id;
              return (
                <CropOptionCard
                  key={`${poseIndex}-${option.id}`}
                  selected={isSelected}
                  onClick={() => handleSelect(poseIndex, option)}
                >
                  <ExampleImageContainer>
                    <ExampleImage
                      src={
                        option.example_image_url ||
                        option.exampleImageUrl ||
                        PLACEHOLDER_IMAGE
                      }
                      alt={`${
                        option.display_name || option.displayName
                      } example`}
                      onError={(e) => {
                        if (e.target.src !== PLACEHOLDER_IMAGE) {
                          e.target.src = PLACEHOLDER_IMAGE;
                        }
                      }}
                    />
                  </ExampleImageContainer>
                  <OptionName>
                    {option.display_name || option.displayName}
                  </OptionName>
                  {option.description && (
                    <OptionDescription>{option.description}</OptionDescription>
                  )}
                  {isSelected && <SelectedIndicator>✓</SelectedIndicator>}
                </CropOptionCard>
              );
            })}
          </CropOptionsGrid>
        </div>
      ))}
    </SelectorWrapper>
  );
};

export default CropOptionsPrepaySelector;
